
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MessageSquare, User, Check } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

// Define the testimonial data structure
interface Testimonial {
  id: number;
  quote: string;
  name: string;
  title: string;
  highlights: string[];
}

// Sample testimonial data (updated to feature women sleep coaches)
const testimonials: Testimonial[] = [
  {
    id: 1,
    quote: "SleepNest saved me 15+ hours per week on data analysis. The AI insights have improved my client success rate by 40%, and I no longer stress about organizing sleep logs.",
    name: "Dr. Sarah Johnson",
    title: "Pediatric Sleep Specialist, Sleep Solutions Inc.",
    highlights: [
      "Saved 15+ hours per week on data analysis",
      "AI insights improved client success rate by 40%",
      "Eliminated stress of manual data sorting"
    ]
  },
  {
    id: 2,
    quote: "My practice efficiency increased by 30% with SleepNest. I'm now able to support more families without compromising quality, and the AI recommendations have been invaluable for my sleep coaching business.",
    name: "Jennifer Matthews",
    title: "Certified Sleep Coach, DreamNight Sleep Solutions",
    highlights: [
      "Practice efficiency increased by 30%",
      "Supporting more families without compromising quality",
      "Eliminated hours of spreadsheet work each day"
    ]
  },
  {
    id: 3,
    quote: "Before SleepNest, I spent hours creating spreadsheets from parent logs. Now the data is organized automatically, and the AI highlights patterns I might have missed. My clients see results in half the time.",
    name: "Emily Chen, PhD",
    title: "Family Sleep Institute Consultant",
    highlights: [
      "Reduced client resolution time by 50%",
      "AI identifies patterns human analysis missed",
      "Automated organization of parent sleep logs"
    ]
  }
];

export function TestimonialSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeTestimonial = testimonials[activeIndex];

  // Auto-rotate testimonials every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % testimonials.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle manual navigation
  const goToTestimonial = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Used by Leading Sleep Specialists to Save Time & Improve Results
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            See what sleep specialists are saying about how SleepNest transforms their practice
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto mb-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTestimonial.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="shadow-lg overflow-hidden border-0">
                <CardContent className="p-8 bg-[#6A5ACD] text-white">
                  <div className="flex items-start mb-6">
                    <MessageSquare className="h-6 w-6 mr-3 flex-shrink-0 mt-1 text-[#FFDAB9]" />
                    <p className="text-white text-lg italic font-medium">"{activeTestimonial.quote}"</p>
                  </div>
                  
                  <Separator className="my-4 bg-[#E6E6FA]/30" />
                  
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center">
                      <div className="bg-[#5A4CB3] rounded-full p-2 mr-3">
                        <User className="text-[#FFDAB9]" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{activeTestimonial.name}</h4>
                        <p className="text-[#FFDAB9] text-sm">{activeTestimonial.title}</p>
                      </div>
                    </div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="text-yellow-300 fill-yellow-300" size={18} />
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {activeTestimonial.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-1">
                          <Check className="h-4 w-4 text-[#E6E6FA]" />
                        </div>
                        <p className="text-sm text-white">{highlight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>

          {/* Testimonial navigation dots */}
          <div className="flex justify-center space-x-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                  index === activeIndex ? 'bg-[#6A5ACD]' : 'bg-gray-300'
                }`}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Trusted organization logos */}
        <div className="mt-12">
          <p className="text-center text-gray-500 text-sm uppercase tracking-wide mb-6">Trusted by leading organizations</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-80">
            <div className="h-10 text-lg font-bold text-gray-500">GentleBirth</div>
            <div className="h-10 text-lg font-bold text-gray-500">FSI</div>
            <div className="h-10 text-lg font-bold text-gray-500">Sleep Solutions</div>
            <div className="h-10 text-lg font-bold text-gray-500">BabySleep+</div>
            <div className="h-10 text-lg font-bold text-gray-500">DreamNight</div>
          </div>
        </div>
      </div>
    </section>
  );
}
